// src\pages\marketing\Projects.js
import React, { useMemo, useState, useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../App";
import { getAuth } from "firebase/auth";
import BigModal from "../../components/BigModal";
import LeadForm from "../../components/LeadForm/LeadForm";
import { track } from "../../analytics/tracker";
import { EV } from "../../analytics/events";
import { getConsent } from "../../analytics/tracker";


const API_ORIGIN = "https://modularobjects.com";
const toAbs = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_ORIGIN}${url.startsWith("/") ? "" : "/"}${url}`;
};


function useFeaturedCount() {
  const getCount = () => {
    const w = window.innerWidth;
    if (w >= 1536) return 5; // 2xl
    if (w >= 1280) return 4; // xl
    if (w >= 1024) return 3; // lg
    if (w >= 640) return 2;  // sm
    return 1;                // base
  };

  const [count, setCount] = useState(getCount);

  useEffect(() => {
    const onResize = () => setCount(getCount());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return count;
}

function pickRandom(arr, n, seed = 1) {
  const a = [...arr];
  let s = seed || 1234567;
  const rand = () => (s = (s * 16807) % 2147483647) / 2147483647;

  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a.slice(0, Math.min(n, a.length));
}


function Badge({ children }) {
  return (
    <span className="text-xs px-2 py-1 rounded bg-white/70 dark:bg-black/20 border border-gray-200 dark:border-gray-800">
      {children}
    </span>
  );
}

function StatusPill({ status }) {
  const { t } = useTranslation();
  const map = {
    built: t("projects.status.built", "Built"),
    in_progress: t("projects.status.in_progress", "In progress"),
    concept: t("projects.status.concept", "Concept"),
  };
  return (
    <span className="text-xs px-2 py-1 rounded bg-primary-light/10 dark:bg-primary-dark/20 border border-gray-200 dark:border-gray-800">
      {map[status] || status}
    </span>
  );
}

const FX_RSD_PER_EUR = 117;

function getDisplayPrice(priceTotal, priceCurrency, uiLang) {
  const lang = String(uiLang || "sr").toLowerCase();
  const isSR = lang.startsWith("sr");

  const total = Number(priceTotal);
  if (!Number.isFinite(total)) return null;

  const cur = String(priceCurrency || "RSD").toUpperCase();

  // locale (možeš proširiti po želji)
  const locale = isSR ? "sr-RS" : "en-US";

  if (isSR) {
    // ✅ SR uvek prikazuje RSD
    if (cur === "EUR") {
      return { amount: total * FX_RSD_PER_EUR, currency: "RSD", locale };
    }
    return { amount: total, currency: "RSD", locale };
  }

  // ✅ svi ostali jezici prikazuju EUR
  if (cur === "RSD") {
    return { amount: total / FX_RSD_PER_EUR, currency: "EUR", locale };
  }

  // ako je ponuda već u EUR (ili bilo šta što tretiraš kao EUR)
  return { amount: total, currency: "EUR", locale };
}

function formatMoney(amount, currency = "RSD", locale = "sr-RS") {
  const n = Number(amount);
  if (!Number.isFinite(n)) return null;

  // EUR često lepše sa 0 decimala u prikazu projekata, ostavi 0 kao i kod tebe
  const maxFrac = 0;

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: maxFrac,
    }).format(n);
  } catch {
    return `${Math.round(n).toLocaleString(locale)} ${currency}`;
  }
}

function ProjectCard({ p, user, uiLang , surface = "projects_list"}) {
  
    const isBuilt = String(p.status || "").trim().toLowerCase() === "built";
    const hasPrice = p.priceTotal != null && Number.isFinite(Number(p.priceTotal));
    const showPrice = isBuilt && hasPrice;


  return (
    <Link
      to={`/projects/${p.slug}`}
      onClick={() => {
  // 1) BigQuery tracking
  track(EV.PROJECT_OPEN, {
    ...getConsent(),
    metadata: {
      surface,          // ili "featured"
      project_slug: p.slug,
      project_id: p.id,
      project_type: p.type,
      project_status: p.status,
      has_docs: !!p.hasDocs,
      has_scan: !!p.hasScan,
      has_viewer: !!p.hasViewer,
      has_photos: !!p.hasPhotos,
      price_total: p.priceTotal ?? null,
      price_currency: p.priceCurrency ?? null,
    },
  });

  // 2) GTM (ostavi ako koristiš)
  window.dataLayer?.push({
    event: "projects_card_click",
    project_slug: p.slug,
    project_type: p.type,
    project_status: p.status,
  });
}}
      className="group bg-background-light dark:bg-background-dark rounded shadow-md border border-gray-100 dark:border-gray-800 hover:shadow-lg transition overflow-hidden"
    >
      <div className="relative">
        <img
          src={p.cover}
          alt={p.title}
          className="
            w-full 
            h-48 
            md:h-56 
            lg:h-72 
            xl:h-80 
            2xl:h-[380px]
            object-cover 
            group-hover:scale-[1.01] 
            transition
          "
          loading="lazy"
        />

     {showPrice && (() => {
  const dp = getDisplayPrice(p.priceTotal, p.priceCurrency || "RSD", uiLang);
  if (!dp) return null;
  return (
    <div className="absolute bottom-3 right-3">
      <div className="bg-gradient-to-r from-black/80 to-black/40 text-primary-light px-4 py-1.5 rounded-lg text-sm md:text-base font-semibold shadow-xl">
        {formatMoney(dp.amount, dp.currency, dp.locale)}
      </div>
    </div>
  );
})()}


        <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
          <StatusPill status={p.status} />
          {p.hasScan && <Badge>3D scan</Badge>}
          {p.hasViewer && <Badge>3D viewer</Badge>}
          {p.hasDocs && <Badge>Docs</Badge>}
        </div>
      </div>

      <div className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-semibold text-lg text-text-light dark:text-text-dark leading-snug">
            {p.title}
          </h3>
        </div>

        {/* ✅ ovde: lokacija + godina + tip + cena */}
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-text-light/80 dark:text-text-dark/80">
            {p.location} • {p.year} • {p.type}
          </p>

        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          {(p.tags || []).slice(0, 4).map((tag) => (
            <Badge key={tag}>{tag}</Badge>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          {p.hasPhotos && <Badge>Photos</Badge>}

          {p.hasDocs && !user && (
            <Link
              to={`/login?next=/projects/${p.slug}`}
              onClick={(e) => {
                e.stopPropagation();
                window.dataLayer?.push({
                  event: "projects_login_for_docs_click",
                  project_slug: p.slug,
                });
              }}
              className="text-xs underline text-primary-light dark:text-primary-dark"
            >
              Log in to access docs
            </Link>
          )}
        </div>
      </div>
    </Link>
  );
}


export default function Projects() {
  const { t, i18n } = useTranslation();
  const { user } = useContext(UserContext);

  const TYPE_OPTIONS = useMemo(() => ([
    { value: "all", label: t("projects.filters.typeAll") },
    { value: "kitchen", label: t("projects.filters.typeKitchen") },
    { value: "wardrobe", label: t("projects.filters.typeWardrobe") },
    { value: "bathroom", label: t("projects.filters.typeBathroom") },
    { value: "other", label: t("projects.filters.typeOther") },
  ]), [t]);

  const STATUS_OPTIONS = useMemo(() => ([
    { value: "all", label: t("projects.filters.statusAll") },
    { value: "built", label: t("projects.filters.statusBuilt") },
    { value: "in_progress", label: t("projects.filters.statusInProgress") },
    { value: "concept", label: t("projects.filters.statusConcept") },
  ]), [t]);

  const SORT_OPTIONS = useMemo(() => ([
    { value: "newest", label: t("projects.filters.sortNewest") },
    { value: "oldest", label: t("projects.filters.sortOldest") },
    { value: "title_asc", label: t("projects.filters.sortTitleAsc") },
  ]), [t]);

  // filters
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [onlyScan, setOnlyScan] = useState(false);
  const [onlyViewer, setOnlyViewer] = useState(false);
  const [onlyDocs, setOnlyDocs] = useState(false);
  const [onlyPhotos, setOnlyPhotos] = useState(false);
  const [sort, setSort] = useState("newest");

  // data
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");

  const [roles, setRoles] = useState([]);
const [rolesLoading, setRolesLoading] = useState(false);

const [openLeadModal, setOpenLeadModal] = useState(false);
const [leadFormKey, setLeadFormKey] = useState(0);

const featuredCount = useFeaturedCount();
const [featuredSeed, setFeaturedSeed] = useState(() => Date.now());



useEffect(() => {
  let alive = true;

  (async () => {
    try {
      if (!user) {
        if (alive) setRoles([]);
        return;
      }
      setRolesLoading(true);

      const auth = getAuth();
      const u = auth.currentUser;
      if (!u) {
        if (alive) setRoles([]);
        return;
      }

      


      const token = await u.getIdToken();

      const res = await fetch("https://modularobjects.com/marketing/api/admin/me", {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });

      const data = await res.json().catch(() => ({}));

console.log("raw sample:", data?.[0]);

      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);

      const arr = Array.isArray(data.roles) ? data.roles : [];
      if (alive) setRoles(arr);
    } catch (e) {
      if (alive) setRoles([]);
    } finally {
      if (alive) setRolesLoading(false);
    }
  })();

  return () => { alive = false; };
}, [user]);

const canManageProjects = roles.some((r) =>
  ["admin", "editor", "marketing_admin", "marketing_editor"].includes(r)
);


  const normalizeProject = (p) => ({
    ...p,
    id: p.id,
    slug: p.slug,
    title: p.title,
    brief: p.brief,
    location: p.location,
    year: p.year,
    type: p.type,
    status: p.status,
    cover: toAbs(p.cover || p.cover_image || p.coverImage),

    tags: Array.isArray(p.tags) ? p.tags : [],

    // snake_case -> camelCase
    hasDocs: !!(p.hasDocs ?? p.has_docs),
    hasScan: !!(p.hasScan ?? p.has_scan),
    hasViewer: !!(p.hasViewer ?? p.has_viewer),
    hasPhotos: !!(p.hasPhotos ?? p.has_photos),

    // ✅ PRICE (iz offer_*.json preko backend-a)
    priceTotal: p.price_total ?? null,
    priceCurrency: p.price_currency ?? null,
    euroRate: p.euro_rate ?? null,
  });

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      setLoadErr("");

      try {
        const lang = (i18n.language || "sr").toLowerCase().startsWith("en") ? "en" : "sr";

const res = await fetch(`https://modularobjects.com/marketing/api/projects?lang=${lang}`, {
  credentials: "include",
});

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        const arr = Array.isArray(data) ? data.map(normalizeProject) : [];
        if (mounted) setProjects(arr);
      } catch (e) {
        console.error("❌ Failed to load projects:", e);
        if (mounted) {
          setProjects([]);
          setLoadErr(e.message || "Failed to load projects");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    let arr = [...projects];

    const query = q.trim().toLowerCase();
    if (query) {
      arr = arr.filter((p) => {
        const hay = [
          p.title,
          p.location,
          p.type,
          p.status,
          ...(p.tags || []),
        ]
          .join(" ")
          .toLowerCase();
        return hay.includes(query);
      });
    }

    if (type !== "all") arr = arr.filter((p) => p.type === type);
    if (status !== "all") arr = arr.filter((p) => p.status === status);
    if (onlyScan) arr = arr.filter((p) => !!p.hasScan);
    if (onlyViewer) arr = arr.filter((p) => !!p.hasViewer);
    if (onlyDocs) arr = arr.filter((p) => !!p.hasDocs);
    if (onlyPhotos) arr = arr.filter((p) => !!p.hasPhotos);

    if (sort === "newest") arr.sort((a, b) => (b.year || 0) - (a.year || 0));
    if (sort === "oldest") arr.sort((a, b) => (a.year || 0) - (b.year || 0));
    if (sort === "title_asc")
      arr.sort((a, b) => (a.title || "").localeCompare(b.title || ""));

    return arr;
  }, [projects, q, type, status, onlyScan, onlyViewer, onlyDocs, onlyPhotos, sort]);

  const featuredPool = useMemo(() => {
  const built = projects.filter((p) => String(p.status).toLowerCase() === "built");
  return built.length ? built : projects; // fallback ako nema built
}, [projects]);

const featured = useMemo(() => {
  // random N koji staje u 1 red
  return pickRandom(featuredPool, featuredCount, featuredSeed);
}, [featuredPool, featuredCount, featuredSeed]);


  return (
    <div className="p-4 space-y-12">
      {/* HERO */}
      <section className="text-center py-14 bg-background-light dark:bg-background-dark rounded shadow-md border border-gray-100 dark:border-none">
        <h1 className="text-4xl font-bold text-primary-light dark:text-primary-dark mb-3">
          {t("projects.heroTitle")}
        </h1>
        <p className="text-lg text-text-light dark:text-text-dark max-w-2xl mx-auto">
          {t("projects.heroSubtitle")}
        </p>

        {canManageProjects && (
  <div className="mb-6">
    <Link className="btn-primary mt-4 inline-block" to="/marketing/admin/projects">
      🛠 Marketing Projects Admin
    </Link>
  </div>
)}


        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/3d-scan"
            className="bg-primary-light dark:bg-primary-dark text-white dark:text-black px-6 py-2 rounded hover:opacity-90 inline-block"
          >
            {t("scanLanding.Title")}
          </Link>
          <Link
            to="/kuhinje"
            className="bg-primary-light/10 dark:bg-primary-dark/20 text-text-light dark:text-text-dark px-6 py-2 rounded border border-gray-200 dark:border-gray-800 hover:opacity-90 inline-block"
          >
            {t("kitchens_title", "Custom Kitchens")}
          </Link>
        </div>

<button
  type="button"
  onClick={() => {
  setOpenLeadModal(true);

  track(EV.CTA_CLICK, {
    ...getConsent(),
    metadata: {
      cta: "projects_request_quote",
      page: "projects",
      surface: "hero",
    },
  });
  track(EV.LEAD_MODAL_OPEN, {
    ...getConsent(),
    metadata: { 
      page:"projects",
      surface:"hero",
      cta:"projects_request_quote" }
  });

  window.dataLayer?.push({ event: "projects_lead_modal_open" });
}}
  className="mt-6 bg-primary-light dark:bg-primary-dark text-white dark:text-black px-6 py-2 rounded hover:opacity-90 inline-block"
>
  {t("projects.ctaRequest")}
</button>


      </section>

      {/* LOAD STATUS */}
      {loading && (
        <div className="p-3 rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-neutral-900">
          {t("projects.loading")}
        </div>
      )}

      {!!loadErr && (
        <div className="p-3 rounded border border-red-300 bg-red-50 text-red-700">
          {t("projects.apiError", { error: loadErr })}{" "}
          <span className="opacity-70">
            {t("projects.apiErrorHint")}
          </span>
        </div>
      )}

      {/* FEATURED */}
      <section className="space-y-4">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-primary-light dark:text-primary-dark">
              {t("projects.featuredTitle")}
            </h2>
            <p className="text-text-light/80 dark:text-text-dark/80">
              {t("projects.featuredSubtitle")}
            </p>
          </div>
          <div className="text-sm text-text-light/70 dark:text-text-dark/70">
            {t("projects.featuredShowing", { count: featured.length })}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
 {featured.map((p) => (
  <ProjectCard key={p.id} p={p} user={user} uiLang={i18n.language} surface="projects_list"/>
))}
</div>

{/* <div className="pt-2 text-right">
  <button
    type="button"
    onClick={() => setFeaturedSeed(Date.now())}
    className="text-sm underline text-text-light/70 dark:text-text-dark/70 hover:opacity-80"
  >
    Shuffle
  </button>
</div> */}


      </section>

      {/* FILTERS + LIST */}
      <section className="space-y-4">
        <div className="bg-background-light dark:bg-background-dark rounded shadow-md border border-gray-100 dark:border-gray-800 p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="text-sm text-text-light/80 dark:text-text-dark/80">
                {t("projects.filters.search")}
              </label>
              <input
                value={q}
                onChange={(e) => {
  const v = e.target.value;
  setQ(v);

  track(EV.FILTER_CHANGE, {
    ...getConsent(),
    metadata: {
      action: "search",
      filter: "sort",
      value: v,
      page: "projects",
    },
  });
}}
                placeholder={t("projects.filters.searchPlaceholder")}
                className="w-full mt-1 px-3 py-2 rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-neutral-900 text-text-light dark:text-text-dark"
              />
            </div>

            <div>
              <label className="text-sm text-text-light/80 dark:text-text-dark/80">
                {t("projects.filters.type")}
              </label>
              <select
                value={type}
                onChange={(e) => {
  const v = e.target.value;
  setType(v);

  track(EV.CATALOG_SEARCH, {
    ...getConsent(),
    metadata: {
      action: "filter_change",
      filter: "type",
      value: v,
      page: "projects",
    },
  });
}}
                className="w-full mt-1 px-3 py-2 rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-neutral-900 text-text-light dark:text-text-dark"
              >
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-text-light/80 dark:text-text-dark/80">
                {t("projects.filters.status")}
              </label>
              <select
                value={status}
                onChange={(e) => {
  const v = e.target.value;
  setStatus(v)

  track(EV.CATALOG_SEARCH, {
    ...getConsent(),
    metadata: {
      action: "filter_change",
      filter: "type",
      value: v,
      page: "projects",
    },
  });
}}
                className="w-full mt-1 px-3 py-2 rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-neutral-900 text-text-light dark:text-text-dark"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-text-light/80 dark:text-text-dark/80">
                {t("projects.filters.sort")}
              </label>
              <select
                value={sort}
                onChange={(e) => {
  const v = e.target.value;
  setSort(v)

  track(EV.CATALOG_SEARCH, {
    ...getConsent(),
    metadata: {
      action: "filter_change",
      filter: "type",
      value: v,
      page: "projects",
    },
  });
}}
                className="w-full mt-1 px-3 py-2 rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-neutral-900 text-text-light dark:text-text-dark"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 mt-4">
            <label className="inline-flex items-center gap-2 text-sm text-text-light dark:text-text-dark">
              <input
                type="checkbox"
                checked={onlyScan}
                onChange={(e) => setOnlyScan(e.target.checked)}
              />
              {t("projects.filters.hasScan")}
            </label>

            <label className="inline-flex items-center gap-2 text-sm text-text-light dark:text-text-dark">
              <input
                type="checkbox"
                checked={onlyViewer}
                onChange={(e) => setOnlyViewer(e.target.checked)}
              />
              {t("projects.filters.hasViewer")}
            </label>

            <label className="inline-flex items-center gap-2 text-sm text-text-light dark:text-text-dark">
              <input
                type="checkbox"
                checked={onlyDocs}
                onChange={(e) => setOnlyDocs(e.target.checked)}
              />
              {t("projects.filters.hasDocs")}
            </label>

            <label className="inline-flex items-center gap-2 text-sm text-text-light dark:text-text-dark">
              <input
                type="checkbox"
                checked={onlyPhotos}
                onChange={(e) => setOnlyPhotos(e.target.checked)}
              />
              {t("projects.filters.hasPhotos")}
            </label>

            <button
              onClick={() => {
                setQ("");
                setType("all");
                setStatus("all");
                setOnlyScan(false);
                setOnlyViewer(false);
                setOnlyDocs(false);
                setOnlyPhotos(false);
                setSort("newest");
                window.dataLayer?.push({ event: "projects_filters_reset" });
              }}
              className="ml-auto bg-primary-light dark:bg-primary-dark text-white dark:text-black px-4 py-2 rounded hover:opacity-90"
            >
              {t("projects.filters.reset")}
            </button>
          </div>
        </div>

        {!loading && filtered.length === 0 ? (
          <div className="text-center py-16 bg-background-light dark:bg-background-dark rounded shadow-md border border-gray-100 dark:border-gray-800">
            <h3 className="text-xl font-semibold text-primary-light dark:text-primary-dark">
              {t("projects.emptyTitle")}
            </h3>
            <p className="text-text-light/80 dark:text-text-dark/80 mt-2">
              {t("projects.emptySubtitle")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
 {filtered.map((p) => (
  <ProjectCard key={p.id} p={p} user={user} uiLang={i18n.language} surface="list"/>
))}
</div>

        )}
      </section>

<BigModal
  open={openLeadModal}
  onClose={() => {
  setOpenLeadModal(false);
  setLeadFormKey((k) => k + 1);

  track(EV.LEAD_MODAL_CLOSE, {
    ...getConsent(),
    metadata: {
      cta: "projects_request_quote",
      surface:"lead_modal",
      page: "projects",
    },
  });

  window.dataLayer?.push({ event: "projects_lead_modal_close" });
}}
  title={t("common.lead.title")}
>
  <LeadForm
  key={leadFormKey}
  mailserverApi="https://modularobjects.com/mailserver"
  context={{ page: "projects" }}
  allowAttachments={true}
  initialMessage={t("projects.leadInitialMessage")}
  
  onSuccess={({ apiResult, error }) => {
    if (error) return;

      if (!(apiResult?.ok || apiResult?.needsVerification)) return;
      
     track(EV.LEAD_SUBMIT_SUCCESS, {
    ...getConsent(),
    contact_id: apiResult?.contactId || apiResult?.contact_id || null,
    metadata: {
      page: "projects",
      surface: "lead_form",
      ok: !!apiResult?.ok,
      needsVerification: !!apiResult?.needsVerification,
    },
  });

      setTimeout(() => {
        setOpenLeadModal(false);
        setLeadFormKey((k) => k + 1);
      }, 1500);
    
  }}
  conversion={() => {
    window.gtag?.("event", "conversion", {
      send_to: "AW-17534918048/_fhiCOLimJUbEKC7pqlB",
      value: 1.0,
      currency: "RSD",
    });
    window.gtag?.("event", "generate_lead", { value: 1.0, currency: "RSD" });
  }}
/>

</BigModal>



    </div>
  );
}
